// Edit planner for CREATE / ALTER / DROP SEQUENCE (v2 Slice D3).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice D3.
//
// All three sequence ops append a fresh statement after the source statement
// (DSE2-7: no coalescing by default). The source statement kind is
// unrestricted — each op names the target sequence directly.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { DdlEdit, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import {
  synthesizeAlterSequence,
  synthesizeCreateSequence,
  synthesizeDropSequence,
} from './dialect.sequence';

type SequenceEdit = Extract<
  DdlEdit,
  { op: 'create-sequence' | 'alter-sequence' | 'drop-sequence' }
>;

export function planSequenceEdit(
  ctx: DdlEditCtx,
  edit: SequenceEdit,
): readonly PlannedByteEdit[] {
  const synth = synthForSequence(ctx, edit);
  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: edit.op,
  }];
}

function synthForSequence(ctx: DdlEditCtx, edit: SequenceEdit): string {
  switch (edit.op) {
    case 'create-sequence':
      return synthesizeCreateSequence(ctx.engine, edit.sequence);
    case 'alter-sequence':
      return synthesizeAlterSequence(
        ctx.engine,
        edit.sequenceName,
        edit.change,
      );
    case 'drop-sequence':
      return synthesizeDropSequence(
        ctx.engine,
        edit.sequenceName,
        edit.ifExists ?? false,
      );
  }
}
